import React, { type JSX } from "react";
import { t } from "ttag";

import { Form } from "../../../forms/Form";
import { FormSubmit } from "../../../forms/FormSubmit";
import { format } from "../../../utils/format";
import { getDinero } from "../../../utils/money";
import { assertNever, guardUnhandledAction } from "../../../utils/never";
import { IPaymentMethodProps } from "../PaymentMethod.interfaces";
import { Bluefin } from "../PaymentMethodBluefin";
import { Cash } from "../PaymentMethodCash";
import { Cybersource } from "../PaymentMethodCybersource";
import { Financing } from "../PaymentMethodFinancing";
import { FinePrint } from "../PaymentMethodFinancingFinePrint";
import { Microform } from "../PaymentMethodMicroform";
import { PayLater } from "../PaymentMethodPayLater";
import {
    CARD_TYPE_AMEX,
    PRIMARY_PAYMENT_METHOD_KEY,
    SECONDARY_PAYMENT_METHOD_KEY,
    getCardNameFromType,
} from "../constants";
import {
    IFinancingPayment,
    IPayment,
    IReduxFormState,
} from "../reducers.interfaces";
import {
    getFinancingData,
    isFortivaAcctNum,
    isNewFinancingAccount,
} from "../utils";

interface IProps extends IPaymentMethodProps {
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null;
    buildFortivaFinancingUpsell?: (grandTotal: string) => JSX.Element | null;
    changeFormField: (fields: Partial<IReduxFormState>) => void;
    setPaymentMethodFields: (
        methodKey: string,
        fields: Partial<IPayment>,
    ) => void;
    resetPaymentMethods: (methods: readonly IPayment["method_type"][]) => void;
    changeCreditCardNumber: (methodKey: string, cardNumber: string) => void;
    changeCreditCardExpDate: (methodKey: string, expDate: string) => void;
    changeAmount: (methodKey: string, amount: string) => void;
    changeFinancingAccountNumber: (
        methodKey: string,
        accountNumber: string,
    ) => void;
    onApplyDenial: () => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
    addSplitPayToFinancing: () => void;
    enableSplitPay: boolean;
    isCSR?: boolean;
    formRef?: React.RefObject<HTMLFormElement | null>;
}

const shouldEnableSubmitButton = (props: IProps): boolean => {
    const data = getFinancingData(props);
    if (data) {
        // if a fotiva account is provided, enable submit button
        if (isFortivaAcctNum(data.financing_account)) {
            return true;
        }
        if (data.new_financing_account && !isSplitPay(props)) {
            // in the case of a new account, we'll use a submit button inside the accordion
            return false;
        }
        if (!data.financing_account) {
            return false;
        }
        if (
            data.amount &&
            props.data.financing_plans.length > 0 &&
            parseFloat(data.amount) <
                parseFloat(
                    props.data.financing_plans[0].product_price_threshold,
                )
        ) {
            // we are below the lowest financing threshold, so cannot finance
            return false;
        }
        // Figure out if credit applications are allowed
        const allowCreditApplications = props.data.financing_plans.reduce(
            (memo, plan) => {
                return memo || plan.allow_credit_application;
            },
            false,
        );
        return allowCreditApplications
            ? data.has_esigned && data.has_agreed
            : data.has_esigned;
    } else {
        // Check all payment methods are ready to submit
        for (const methodKey of Object.keys(props.form.payment_methods)) {
            const method = props.form.payment_methods[methodKey];
            if (
                method.method_type === "cybersource-microform" &&
                !method.transient_token
            ) {
                return false;
            }
        }
        return true;
    }
};

const shouldShowSubmitButton = (props: IProps): boolean => {
    // In split pay mode, always show the main submit button since the
    // accordion's submit button is hidden (gated behind isTotalCovered).
    if (isSplitPay(props)) {
        return true;
    }
    // in the case of a new account, we'll use a submit button inside the accordion
    return !isNewFinancingAccount(props);
};

const shouldShowFinePrint = (props: IProps): boolean => {
    // check for Fortiva
    const data = getFinancingData(props);
    if (data) {
        // if it's a Fortiva Account, hide the fineprint
        if (isFortivaAcctNum(data.financing_account)) {
            return false;
        }
    }

    // in the case of a new account, we'll use fine print inside the accordion
    return !isNewFinancingAccount(props);
};

const isSplitPay = (props: IProps): boolean => {
    return SECONDARY_PAYMENT_METHOD_KEY in props.form.payment_methods;
};

const getCreditCardLabel = (cardIndex?: 1 | 2): string => {
    return cardIndex ? t`Credit Card ${cardIndex}` : t`Credit Card`;
};

const maskAccountNumber = (
    accountNumber: string | null | undefined,
    paymentMethod: IPayment,
): string => {
    if (!accountNumber) {
        return "";
    }

    const amex_mask = "**** ****** *";
    const standard_mask = "**** **** **** ";

    const methodType = paymentMethod.method_type;
    if (methodType === "cybersource-microform") {
        // The transient token only carries a masked number, so mask by the
        // detected card type. The legacy method masks its *formatted* number,
        // which consumes the whole mask, so both render the same shape.
        const mask =
            paymentMethod.card_type === CARD_TYPE_AMEX
                ? amex_mask
                : standard_mask;
        return mask + accountNumber.substr(-4);
    } else if (methodType === "bluefin") {
        // unfortunately this makes assumptions about bluefin encrypted string formatting
        const number_start = accountNumber.indexOf(";") + 1;
        const number_end = accountNumber.indexOf("=");
        const mask =
            number_end - number_start === 15 ? amex_mask : standard_mask;
        return mask + accountNumber.substring(number_end - 4, number_end);
    } else {
        const mask =
            methodType === "cybersource" &&
            paymentMethod.card_type === CARD_TYPE_AMEX
                ? amex_mask
                : standard_mask;
        return accountNumber
            ? mask.substr(0, Math.max(accountNumber.length - 4, 0)) +
                  accountNumber.substr(-4)
            : "";
    }
};

const isReadyToSubmit = (
    methodKey: string,
    methodType: string,
    props: IProps,
): boolean => {
    if (methodType === "cybersource-microform") {
        const method = props.form.payment_methods[methodKey];
        if (
            method.method_type === "cybersource-microform" &&
            !method.transient_token
        ) {
            return false;
        }
        return true;
    } else if (methodType === "cybersource") {
        const errors = props.errors[methodKey];
        if (
            (errors.card_cvc && errors.card_cvc.length) ||
            (errors.card_expiration && errors.card_expiration.length) ||
            (errors.card_number && errors.card_number.length)
        ) {
            return false;
        }
    } else if (methodType === "financing") {
        const errors = props.errors[methodKey];
        const method = props.form.payment_methods[
            methodKey
        ] as IFinancingPayment;
        if (
            (errors.financing_account && errors.financing_account.length) ||
            !method.has_agreed ||
            !method.has_esigned
        ) {
            return false;
        }
    } else if (methodType === "bluefin") {
        const errors = props.errors[methodKey];
        if (errors.payment_data && errors.payment_data.length) {
            return false;
        }
    }
    return true;
};

const buildMethodForm = (props: IProps): React.ReactNode => {
    const formProps = {
        form: props.form,
        data: props.data,
        derived: props.derived,
        gifts: props.gifts,
        errors: props.errors,
        onValidStateChange: props.onValidStateChange,
        showErrorMessages: props.showErrorMessages,
    };

    const splitProps: {
        [method_key: string]: {
            header: string | null;
            disabled: boolean;
            freezeAmount: boolean;
        };
    } = {
        [PRIMARY_PAYMENT_METHOD_KEY]: {
            header: null,
            disabled: false,
            freezeAmount: false,
        },
        [SECONDARY_PAYMENT_METHOD_KEY]: {
            header: null,
            disabled: false,
            freezeAmount: true,
        },
    };

    if (isSplitPay(props)) {
        // assign headers to the payment forms
        const primaryType =
            props.form.payment_methods[PRIMARY_PAYMENT_METHOD_KEY].method_type;
        if (
            primaryType === "cybersource" ||
            primaryType === "cybersource-microform" ||
            primaryType === "bluefin"
        ) {
            splitProps[PRIMARY_PAYMENT_METHOD_KEY].header =
                getCreditCardLabel(1);
            splitProps[SECONDARY_PAYMENT_METHOD_KEY].header =
                getCreditCardLabel(2);
        } else {
            splitProps[PRIMARY_PAYMENT_METHOD_KEY].header = "Financing";
            splitProps[SECONDARY_PAYMENT_METHOD_KEY].header =
                getCreditCardLabel();
        }
        // do we have credit card or account information?
        let hasCardInfo = false;
        const primaryMethod =
            props.form.payment_methods[PRIMARY_PAYMENT_METHOD_KEY];
        if (primaryMethod.method_type === "cybersource") {
            hasCardInfo = primaryMethod.card_number.length > 0;
        } else if (primaryMethod.method_type === "cybersource-microform") {
            hasCardInfo = primaryMethod.transient_token.length > 0;
        } else if (primaryMethod.method_type === "bluefin") {
            hasCardInfo = primaryMethod.payment_data.length > 0;
        } else if (primaryMethod.method_type === "financing") {
            hasCardInfo = primaryMethod.financing_account.length > 0;
        }
        // if we have card info and payment states, we might wish to disable one of the forms
        if (props.data.payment_states && hasCardInfo) {
            const methodStates =
                props.data.payment_states.payment_method_states;
            // disable form if a payment is complete
            splitProps[PRIMARY_PAYMENT_METHOD_KEY].disabled =
                PRIMARY_PAYMENT_METHOD_KEY in methodStates &&
                methodStates[PRIMARY_PAYMENT_METHOD_KEY].status === "Complete";
            splitProps[SECONDARY_PAYMENT_METHOD_KEY].disabled =
                SECONDARY_PAYMENT_METHOD_KEY in methodStates &&
                methodStates[SECONDARY_PAYMENT_METHOD_KEY].status ===
                    "Complete";
            // disable PRIMARY amount field if the SECONDARY payment is complete
            splitProps[PRIMARY_PAYMENT_METHOD_KEY].freezeAmount =
                splitProps[SECONDARY_PAYMENT_METHOD_KEY].disabled;
        }
    }

    const form = Object.keys(props.form.payment_methods)
        .sort()
        .map((methodKey) => {
            const methodType =
                props.form.payment_methods[methodKey].method_type;
            switch (methodType) {
                case "cybersource":
                    return (
                        <Cybersource
                            {...formProps}
                            setPaymentMethodFields={
                                props.setPaymentMethodFields
                            }
                            changeCreditCardNumber={
                                props.changeCreditCardNumber
                            }
                            changeCreditCardExpDate={
                                props.changeCreditCardExpDate
                            }
                            changeAmount={props.changeAmount}
                            header={splitProps[methodKey].header}
                            isSplitPay={isSplitPay(props)}
                            shouldFreezeAmount={
                                splitProps[methodKey].freezeAmount
                            }
                            disabled={splitProps[methodKey].disabled}
                            methodKey={methodKey}
                            key={methodKey}
                        />
                    );
                case "bluefin":
                    return (
                        <Bluefin
                            {...formProps}
                            setPaymentMethodFields={
                                props.setPaymentMethodFields
                            }
                            changeAmount={props.changeAmount}
                            header={splitProps[methodKey].header}
                            isSplitPay={isSplitPay(props)}
                            shouldFreezeAmount={
                                splitProps[methodKey].freezeAmount
                            }
                            disabled={splitProps[methodKey].disabled}
                            methodKey={methodKey}
                            key={methodKey}
                        />
                    );
                case "cybersource-microform":
                    return (
                        <Microform
                            {...formProps}
                            changeAmount={props.changeAmount}
                            header={splitProps[methodKey].header}
                            isSplitPay={isSplitPay(props)}
                            shouldFreezeAmount={
                                splitProps[methodKey].freezeAmount
                            }
                            disabled={splitProps[methodKey].disabled}
                            methodKey={methodKey}
                            key={methodKey}
                        />
                    );
                case "financing":
                    return (
                        <Financing
                            {...formProps}
                            setPaymentMethodFields={
                                props.setPaymentMethodFields
                            }
                            resetPaymentMethods={props.resetPaymentMethods}
                            buildFinancingUpsell={props.buildFinancingUpsell}
                            buildFortivaFinancingUpsell={
                                props.buildFortivaFinancingUpsell
                            }
                            changeFinancingAccountNumber={
                                props.changeFinancingAccountNumber
                            }
                            changeAmount={props.changeAmount}
                            onApplyDenial={props.onApplyDenial}
                            header={splitProps[methodKey].header}
                            isSplitPay={isSplitPay(props)}
                            shouldFreezeAmount={
                                splitProps[methodKey].freezeAmount
                            }
                            disabled={splitProps[methodKey].disabled}
                            methodKey={methodKey}
                            key={methodKey}
                            addSplitPayToFinancing={
                                props.addSplitPayToFinancing
                            }
                            enableSplitPay={props.enableSplitPay}
                        />
                    );
                case "cash":
                    return <Cash key={methodKey} />;
                case "pay-later":
                    return <PayLater key={methodKey} />;
                case "default":
                    return null;
                default:
                    guardUnhandledAction(methodType);
            }
            throw assertNever(methodType);
        });

    return form;
};

const buildSummary = (props: IProps): React.ReactNode => {
    if (isSplitPay(props)) {
        return (
            <div>
                <h3 className="checkout-step__split-pay--heading">
                    Split Pay Summary
                </h3>
                {Object.keys(props.form.payment_methods).map((methodKey) => {
                    const paymentMethod = props.form.payment_methods[methodKey];
                    const methodType = paymentMethod.method_type;

                    const amount = getDinero(paymentMethod.amount || "0.00");

                    let accountNumber;
                    let accountType;
                    switch (methodType) {
                        // Both credit card methods summarize the same way. They
                        // store different things in card_number (legacy: the
                        // space-formatted number the user typed; Microform: the
                        // masked value from the transient token), which is why
                        // maskAccountNumber still branches on method_type.
                        case "cybersource":
                        case "cybersource-microform":
                            if (
                                !isReadyToSubmit(methodKey, methodType, props)
                            ) {
                                break;
                            }
                            accountNumber = paymentMethod.card_number;
                            accountType = getCardNameFromType(
                                paymentMethod.card_type,
                            );
                            break;
                        case "financing":
                            if (
                                !isReadyToSubmit(methodKey, methodType, props)
                            ) {
                                break;
                            }
                            accountNumber = paymentMethod.financing_account;
                            if (paymentMethod.financing_plan) {
                                const plan_id = paymentMethod.financing_plan;
                                const selected_plan =
                                    props.data.financing_plans.find((plan) => {
                                        return plan.id === plan_id;
                                    });
                                if (selected_plan) {
                                    accountType = `Financing | Selected term: ${selected_plan.term_months} months`;
                                }
                            }
                            break;
                        case "bluefin":
                            if (
                                !isReadyToSubmit(methodKey, methodType, props)
                            ) {
                                break;
                            }
                            accountNumber = paymentMethod.payment_data;
                            accountType = "Secure Credit Card";
                            break;
                        case "cash":
                        case "pay-later":
                        case "default":
                            break;
                        default:
                            guardUnhandledAction(methodType);
                    }
                    return (
                        <div
                            className="checkout-step__split-pay--row"
                            key={methodKey}
                        >
                            <p>
                                <span>
                                    {maskAccountNumber(
                                        accountNumber,
                                        paymentMethod,
                                    )}
                                </span>
                                <span>{format.money(amount)}</span>
                            </p>
                            <small>{accountType}</small>
                        </div>
                    );
                })}
            </div>
        );
    } else {
        return null;
    }
};

export const PaymentMethod = (props: IProps): React.ReactNode => {
    return (
        <div
            className="checkout-step__payment-method"
            id="payment-method-instructions"
        >
            <Form
                className="checkout-step__payment-form"
                onSubmit={props.onSubmit}
                ref={props.formRef}
            >
                {buildMethodForm(props)}
                {buildSummary(props)}
                {shouldShowSubmitButton(props) && (
                    <FormSubmit
                        disabled={!shouldEnableSubmitButton(props)}
                        className="button button--full-width button--place-order checkout-place-order-button--cc"
                        value={t`Place Order`}
                    />
                )}
                {!isNewFinancingAccount(props) && (
                    <FinePrint showFinePrint={shouldShowFinePrint(props)} />
                )}
            </Form>
        </div>
    );
};
